import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, PackageCheck, ShoppingBag } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCheckout } from "@/hooks/useCheckout";
import Logo from "@/components/Logo";
import SafeZones from "@/components/SafeZones";

export default function Account() {
  const { user, loading, logout, openAuth } = useAuth();
  const { start } = useCheckout();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [fetching, setFetching] = useState(true);
  const loggingOut = useRef(false);

  const loadOrders = useCallback(async () => {
    try {
      const { data } = await api.get("/orders");
      setOrders(data);
    } catch (e) {
      setOrders([]);
    } finally {
      setFetching(false);
    }
  }, []);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      // Not logged in: prompt sign-in (modal). Skip if we're intentionally logging out.
      if (!loggingOut.current) openAuth();
      return;
    }
    // Resume a purchase the user started before logging in
    const pending = localStorage.getItem("kiddio_pending_plan");
    if (pending) {
      localStorage.removeItem("kiddio_pending_plan");
      try {
        const { planId, billing } = JSON.parse(pending);
        start(planId, billing);
        return;
      } catch (e) {}
    }
    loadOrders();
  }, [loading, user, openAuth, start, loadOrders]);

  const handleLogout = async () => {
    loggingOut.current = true;
    await logout();
    navigate("/");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#2E9EF0]">
        <Logo size={48} showText={false} />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#2E9EF0] gap-6 px-5 text-center" data-testid="account-signin-prompt">
        <Logo size={48} showText={false} />
        <p className="text-[17px] font-bold text-[#12263A] max-w-xs">Inicia sesión para ver tu cuenta y tus pedidos.</p>
        <div className="flex gap-3">
          <button
            onClick={() => openAuth()}
            data-testid="prompt-signin-btn"
            className="px-6 py-3 rounded-[18px] bg-[#12263A] text-white border-2 border-[#12263A] font-bold hover:bg-white hover:text-[#12263A] transition-colors"
          >
            Iniciar sesión
          </button>
          <button
            onClick={() => navigate("/")}
            className="px-6 py-3 rounded-[18px] bg-transparent text-[#12263A] border-2 border-[#12263A] font-bold hover:bg-white transition-colors"
          >
            Volver al inicio
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F3F2F2]" data-testid="account-page">
      <header className="bg-[#F3F2F2] border-b-2 border-[#12263A]">
        <div className="max-w-4xl mx-auto px-5 h-16 flex items-center justify-between">
          <button onClick={() => navigate("/")} aria-label="Inicio"><Logo size={38} /></button>
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className="flex items-center gap-2 px-4 py-2 rounded-[16px] border-2 border-[#12263A] font-bold text-[#12263A] hover:bg-[#D6D9DC] transition-colors"
          >
            <LogOut size={18} strokeWidth={2} /> Salir
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-5 py-12">
        <div className="flex items-center gap-4">
          {user.picture ? (
            <img src={user.picture} alt={user.name} className="w-14 h-14 border-2 border-[#12263A]" style={{ borderRadius: "31%" }} />
          ) : (
            <span className="w-14 h-14 flex items-center justify-center bg-[#2E9EF0] border-2 border-[#12263A] font-black text-[#12263A]" style={{ borderRadius: "31%" }}>
              {user.name?.charAt(0)}
            </span>
          )}
          <div>
            <h1 className="text-[28px] md:text-[40px] font-black text-[#12263A] leading-tight">Hola, {user.name?.startsWith("Familia") ? user.name : user.name?.split(" ")[0]}</h1>
            <p className="text-[15px] text-[#6E7B8B]">{user.email}</p>
          </div>
        </div>

        <section className="mt-12">
          <span className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#2E9EF0]">Mis pedidos</span>
          <h2 className="mt-2 text-[24px] font-black text-[#12263A]">Tus compras Kiddio</h2>

          {fetching ? (
            <p className="mt-6 text-[15px] text-[#6E7B8B]">Cargando pedidos…</p>
          ) : orders.length === 0 ? (
            <div className="mt-6 bg-white border-2 border-[#12263A] p-8 flex flex-col items-start gap-4" style={{ borderRadius: "28px" }} data-testid="no-orders">
              <div className="w-12 h-12 flex items-center justify-center bg-[#F3F2F2] border-2 border-[#12263A]" style={{ borderRadius: "31%" }}>
                <ShoppingBag size={24} strokeWidth={2} className="text-[#12263A]" />
              </div>
              <p className="text-[17px] text-[#12263A]">Todavía no tienes pedidos. Elige tu Kiddio y empieza tranquilo.</p>
              <button
                onClick={() => navigate("/")}
                className="px-6 py-3 rounded-[18px] bg-[#2E9EF0] text-[#12263A] border-2 border-[#12263A] font-bold hover:bg-[#12263A] hover:text-[#2E9EF0] transition-colors"
                data-testid="go-buy-btn"
              >
                Ver planes
              </button>
            </div>
          ) : (
            <div className="mt-6 space-y-4">
              {orders.map((o) => (
                <div
                  key={o.session_id}
                  data-testid="order-row"
                  className="bg-white border-2 border-[#12263A] p-6 flex items-center justify-between gap-4"
                  style={{ borderRadius: "24px" }}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-11 h-11 flex items-center justify-center bg-[#3CB878] border-2 border-[#12263A]" style={{ borderRadius: "31%" }}>
                      <PackageCheck size={22} strokeWidth={2} className="text-[#12263A]" />
                    </div>
                    <div>
                      <span className="block font-black text-[#12263A] text-[17px]">{o.plan_name}</span>
                      {o.billing_label && <span className="block text-[12px] font-bold text-[#2E9EF0]">{o.billing_label}</span>}
                      <span className="block text-[13px] text-[#6E7B8B]">
                        {o.created_at ? new Date(o.created_at).toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" }) : ""}
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="block font-black text-[#12263A]">${o.amount}</span>
                    <span className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#3CB878]">Pagado</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        <SafeZones />
      </main>
    </div>
  );
}
